import { deserialisePreset, PRESET_VERSION, type PresetFile } from '../core/export/preset';
import { DEFAULT_PALETTE, type PaletteSpec } from '../core/palette';
import type { ParamValues } from '../core/params/types';
import type { BlendMode } from '../store/layer';
import { TEXT_TECHNIQUE_ID } from '../store/layer';
import type { StudioDocument } from '../store/studioStore';

/**
 * Designed starting points.
 *
 * These are not demonstrations of what each technique can do — the layer picker already
 * does that. They are finished pictures: a typeface, a canvas, a palette, a background
 * and one or two techniques tuned against each other, chosen so that loading one and
 * changing nothing already looks like a decision rather than a default.
 *
 * Each is built as a *partial* document and then run through the same
 * `deserialisePreset` path a user's saved file takes. That means a starter can name only
 * what it actually cares about, everything else comes from the current schema defaults,
 * and a preset written today keeps loading after a parameter is added, removed or
 * re-ranged tomorrow — the same guarantee user presets get, from the same code.
 */
export interface StarterPreset {
  id: string;
  name: string;
  /** One line on what the picture is, shown under the name. */
  note: string;
  build(): StudioDocument;
}

interface LayerSpec {
  technique: string;
  values?: ParamValues;
  opacity?: number;
  blendMode?: BlendMode;
  seed?: number;
  name?: string;
}

interface Spec {
  text: string;
  font: string;
  canvas?: [number, number];
  seed?: number;
  /** Overrides on the text geometry schema. */
  type?: ParamValues;
  /** Overrides on the text *layer* — colour, render mode, stroke. */
  ink?: ParamValues;
  textVisible?: boolean;
  textOpacity?: number;
  textBlend?: BlendMode;
  /** Text layer position in the stack: on top by default, `under` puts it at the bottom. */
  textOn?: 'top' | 'bottom';
  background?: ParamValues;
  post?: Record<string, ParamValues | true>;
  layers: LayerSpec[];
}

/**
 * Shorthand for a curated palette, since almost every preset sets one.
 *
 * Every curated ramp runs dark to light. That means the low end of a ramp disappears
 * into a dark background and the high end into a light one, so several presets below
 * pass `reverse: true` — not as decoration but because otherwise a third of the drawing
 * is invisible against its own backdrop.
 */
function pal(id: string, extra: Partial<PaletteSpec> = {}): PaletteSpec {
  return { ...DEFAULT_PALETTE, source: 'curated', paletteId: id, ...extra };
}

function build(spec: Spec): StudioDocument {
  const [width, height] = spec.canvas ?? [1080, 1350];

  const textLayer = {
    id: 'text',
    techniqueId: TEXT_TECHNIQUE_ID,
    name: 'Text',
    visible: spec.textVisible !== false,
    opacity: spec.textOpacity ?? 1,
    blendMode: spec.textBlend ?? ('normal' as BlendMode),
    seed: 1,
    seedLocked: false,
    values: spec.ink ?? {},
    bulk: {},
    collapsed: false,
  };

  const techniqueLayers = spec.layers.map((layer, i) => ({
    id: `layer-${i}`,
    techniqueId: layer.technique,
    name: layer.name ?? '',
    visible: true,
    opacity: layer.opacity ?? 1,
    blendMode: layer.blendMode ?? ('normal' as BlendMode),
    seed: layer.seed ?? 1000 + i * 137,
    seedLocked: false,
    values: layer.values ?? {},
    bulk: {},
    collapsed: false,
  }));

  const postFx: Record<string, { enabled: boolean; values: ParamValues }> = {};
  for (const [id, values] of Object.entries(spec.post ?? {})) {
    postFx[id] = { enabled: true, values: values === true ? {} : values };
  }

  const file: PresetFile = {
    format: 'typo-studio-preset',
    version: PRESET_VERSION,
    name: 'starter',
    createdAt: new Date(0).toISOString(),
    doc: {
      textValues: { text: spec.text, ...spec.type },
      textBulk: {},
      fontId: spec.font,
      anchorX: 'center',
      anchorY: 'middle',
      canvas: { width, height, exportScale: 2 },
      background: spec.background ?? {},
      postFx,
      seed: spec.seed ?? 7,
      seedLocked: false,
      layers: spec.textOn === 'bottom' ? [textLayer, ...techniqueLayers] : [...techniqueLayers, textLayer],
      activeLayerId: techniqueLayers[0]?.id ?? 'text',
    } as unknown as StudioDocument,
  };

  return deserialisePreset(file).doc;
}

export const STARTER_PRESETS: StarterPreset[] = [
  {
    id: 'stroemung',
    name: 'Strömung',
    note: 'Zweihunderttausend Partikel weichen dem Wort aus und zeichnen dabei seine Umrisse in die Luft.',
    build: () =>
      build({
        text: 'FLUSS',
        font: 'anton',
        seed: 4211,
        type: { autoFitPercent: 0.86, tracking: 0.04 },
        background: { kind: 'solid', colorA: '#07080d' },
        textVisible: false,
        layers: [
          {
            technique: 'flow-field',
            seed: 88,
            values: {
              // Kept well below what the technique allows: a starting point that takes
              // fifteen seconds to appear is not a starting point.
              particles: 9000,
              lifetime: 210,
              stepSize: 1.9,
              passes: 2,
              noiseScale: 0.0016,
              octaves: 3,
              curl: true,
              textMode: 'repel',
              textStrength: 3.4,
              textRadius: 96,
              origin: 'outline',
              lineWidth: 0.6,
              widthMode: 'life',
              opacity: 0.26,
              colorMode: 'distance',
              palette: pal('neon'),
            },
          },
        ],
        post: { bloom: { threshold: 0.5, radius: 34, intensity: 0.55 }, vignette: { amount: 0.35 } },
      }),
  },

  {
    id: 'blasen',
    name: 'Blasen',
    note: 'Kreise füllen jeden freien Millimeter um die Buchstaben, gross am Rand, fein am Text.',
    build: () =>
      build({
        text: 'LUFT',
        font: 'bebas-neue',
        canvas: [1080, 1080],
        seed: 991,
        type: { autoFitPercent: 0.8, tracking: 0.06 },
        background: { kind: 'radial', colorA: '#141824', colorB: '#05060a', radius: 1.1 },
        ink: { color: '#f4f4f8' },
        layers: [
          {
            technique: 'circle-packing',
            seed: 12,
            values: {
              candidates: 11000,
              gap: 1.6,
              textGap: 5,
              placement: 'poisson',
              shape: 'circle',
              style: 'mixed',
              fillRatio: 0.45,
              rings: 3,
              lineWidth: 1.1,
              distanceReach: 420,
              colorMode: 'distance',
              opacity: 0.95,
              palette: pal('riso'),
            },
          },
        ],
      }),
  },

  {
    id: 'scherben',
    name: 'Scherben',
    note: 'Voronoi-Zellen drängen sich am Text und weiten sich nach aussen — ein Sprung im Glas.',
    build: () =>
      build({
        text: 'BRUCH',
        font: 'archivo',
        seed: 66,
        type: { autoFitPercent: 0.82, weight: 800, tracking: -0.01 },
        background: { kind: 'solid', colorA: '#0d0d10' },
        ink: { color: '#eceff4' },
        layers: [
          {
            technique: 'voronoi',
            seed: 405,
            values: {
              points: 2400,
              distribution: 'nearText',
              distributionDistance: 260,
              relaxation: 3,
              style: 'inset',
              inset: 2,
              lineWidth: 0.9,
              // Outside only: the shards crowd around a solid word instead of paving
              // over it, which is the difference between a crack and a mosaic.
              clip: 'outside',
              colorMode: 'distance',
              opacity: 1,
              palette: pal('ink'),
            },
          },
        ],
        post: { grain: { amount: 0.12 } },
      }),
  },

  {
    id: 'wurzelwerk',
    name: 'Wurzelwerk',
    note: 'Wurzeln wachsen aus dem Wort nach unten, weichen ihm aus und werden nach unten hin dünner.',
    build: () =>
      build({
        text: 'GRUND',
        font: 'cormorant-garamond',
        seed: 3311,
        type: { autoFitPercent: 0.7, offsetY: -0.16, tracking: 0.12, weight: 300 },
        background: { kind: 'solid', colorA: '#faf6ee' },
        ink: { color: '#20180f' },
        layers: [
          {
            technique: 'roots',
            seed: 77,
            values: {
              strands: 420,
              depth: 8,
              segmentLength: 30,
              angle: 27,
              jitter: 30,
              gravity: 0.6,
              repel: 1.1,
              baseWidth: 2.4,
              taper: 0.78,
              opacity: 0.75,
              colorMode: 'depth',
              palette: pal('earth'),
            },
          },
        ],
      }),
  },

  {
    id: 'schleimspur',
    name: 'Schleimspur',
    note: 'Ein Physarum-Netz sucht sich seinen Weg zum Wort und lässt die Adern stehen, die es gefunden hat.',
    build: () =>
      build({
        text: 'NETZ',
        font: 'space-grotesk',
        canvas: [1080, 1080],
        seed: 1717,
        type: { autoFitPercent: 0.68, weight: 700, tracking: 0.2 },
        background: { kind: 'solid', colorA: '#04060a' },
        ink: { color: '#f5ede2' },
        textOpacity: 0.95,
        layers: [
          {
            technique: 'physarum',
            seed: 909,
            values: {
              agents: 70000,
              sensorAngle: 22,
              sensorDistance: 13,
              turnSpeed: 28,
              deposit: 0.32,
              diffusion: 0.5,
              decay: 0.022,
              // Obstacle rather than nutrient: the mould crowds up against the letters
              // and leaves them as clean negative space. As a nutrient it grows *over*
              // the word and the typography disappears.
              textRole: 'obstacle',
              textStrength: 1.4,
              resolution: 560,
              totalSteps: 300,
              contrast: 2.6,
              palette: pal('ember'),
            },
          },
        ],
      }),
  },

  {
    id: 'zeitungsdruck',
    name: 'Zeitungsdruck',
    note: 'Ein grobes Punktraster im Vierfarbwinkel — die Schrift zerfällt beim Näherkommen in Punkte.',
    build: () =>
      build({
        text: 'DRUCK',
        font: 'playfair-display',
        seed: 12,
        type: { autoFitPercent: 0.88, weight: 900 },
        background: { kind: 'solid', colorA: '#f2ede2' },
        textVisible: false,
        layers: [
          {
            technique: 'halftone',
            seed: 5,
            values: {
              shape: 'dot',
              pitch: 13,
              angle: 15,
              maxSize: 0.95,
              cmyk: true,
              cmykSpread: 0.7,
              opacity: 0.95,
              palette: pal('riso'),
            },
          },
        ],
        post: { paper: { amount: 0.35, scale: 11 }, grain: { amount: 0.1 } },
      }),
  },

  {
    id: 'stich',
    name: 'Kupferstich',
    note: 'Kreuzschraffur füllt die Buchstaben: eng, wo sie dick sind, offen, wo sie auslaufen.',
    build: () =>
      build({
        text: 'LINIE',
        font: 'bodoni-moda',
        seed: 401,
        type: { autoFitPercent: 0.84, weight: 700, tracking: 0.03 },
        background: { kind: 'solid', colorA: '#f7f4ec' },
        textVisible: false,
        layers: [
          {
            technique: 'hatching',
            seed: 31,
            values: {
              layers: 2,
              angle: 38,
              layerAngle: 62,
              spacing: 5,
              lineWidth: 1.1,
              region: 'inside',
              depthReach: 60,
              // Solid, not depth-shaded: an engraving is one ink, and the depth ramp put
              // the pale end of the palette on a cream page, where it vanished.
              colorMode: 'solid',
              opacity: 1,
              palette: pal('mono'),
            },
          },
        ],
        post: { paper: { amount: 0.25, scale: 8 } },
      }),
  },

  {
    id: 'hoehenlinien',
    name: 'Höhenlinien',
    note: 'Die Distanzkarte des Textes, gelesen wie eine Landkarte — mit einer Prise Rauschen im Gelände.',
    build: () =>
      build({
        text: 'RELIEF',
        font: 'ibm-plex-mono',
        seed: 8,
        type: { autoFitPercent: 0.86, tracking: 0.06, weight: 600 },
        background: { kind: 'solid', colorA: '#08110f' },
        textVisible: false,
        layers: [
          {
            technique: 'contour-lines',
            seed: 202,
            values: {
              levels: 30,
              // Just enough noise to make the rings wander; more and the letterform is
              // no longer readable in them.
              noiseMix: 0.07,
              noiseScale: 0.0022,
              octaves: 3,
              warp: 0,
              resolution: 2,
              lineWidth: 1.1,
              clip: 'all',
              opacity: 0.95,
              palette: pal('moss', { reverse: true }),
            },
          },
        ],
      }),
  },

  {
    id: 'terminal',
    name: 'Terminal',
    note: 'Das Wort in Zeichen aufgelöst, dichter wo es dunkel ist — und dahinter ein blasses Grundrauschen.',
    build: () =>
      build({
        text: 'CODE',
        font: 'jetbrains-mono',
        canvas: [1080, 1080],
        seed: 1024,
        type: { autoFitPercent: 0.8, weight: 700, tracking: 0.05 },
        background: { kind: 'solid', colorA: '#04070a' },
        textVisible: false,
        layers: [
          {
            technique: 'ascii-mosaic',
            seed: 3,
            values: {
              cell: 13,
              mapping: 'distance',
              distanceReach: 150,
              fontSize: 1.2,
              background: true,
              backgroundOpacity: 0.14,
              colorMode: 'distance',
              opacity: 1,
              palette: pal('acid'),
            },
          },
        ],
        post: { bloom: { threshold: 0.7, radius: 16, intensity: 0.4 }, vignette: { amount: 0.45 } },
      }),
  },

  {
    id: 'signalstoerung',
    name: 'Signalstörung',
    note: 'Waagerechte Schnitte, verschobene Kanäle, ein Bild kurz vor dem Abbruch.',
    build: () =>
      build({
        text: 'FEHLER',
        font: 'archivo',
        seed: 5150,
        type: { autoFitPercent: 0.9, weight: 900, tracking: -0.02 },
        background: { kind: 'solid', colorA: '#08080c' },
        textVisible: false,
        layers: [
          {
            technique: 'glitch',
            seed: 66,
            values: {
              slices: 44,
              shift: 120,
              probability: 0.7,
              repeat: 2,
              rgbSplit: 14,
              pixelSort: true,
              sortThreshold: 0.3,
              opacity: 1,
              colorMode: 'gradient',
              palette: pal('cyberpunk'),
            },
          },
        ],
        post: { chromatic: { offset: 6 }, grain: { amount: 0.2 } },
      }),
  },

  {
    id: 'sog',
    name: 'Sog',
    note: 'Vielfach verzerrte Kopien der Kontur, im Kern noch lesbar, an den Rändern längst fortgetragen.',
    build: () =>
      build({
        text: 'STROM',
        font: 'syne',
        seed: 314,
        type: { autoFitPercent: 0.82, weight: 800, tracking: 0.02 },
        background: { kind: 'linear', colorA: '#120a18', colorB: '#04040a', angle: 100 },
        textVisible: false,
        layers: [
          {
            technique: 'domain-warp',
            seed: 27,
            values: {
              strength: 120,
              passes: 3,
              noiseScale: 0.0016,
              octaves: 4,
              scalePerPass: 1.6,
              legibility: 0.45,
              legibilityReach: 46,
              copies: 22,
              style: 'stroke',
              lineWidth: 1.7,
              opacity: 0.5,
              palette: pal('neon', { reverse: true }),
            },
          },
        ],
        post: { bloom: { threshold: 0.55, radius: 40, intensity: 0.5 } },
      }),
  },

  {
    id: 'skelett',
    name: 'Skelett',
    note: 'Nur die Mittelachsen der Buchstaben, so dick wie die Form an dieser Stelle war.',
    build: () =>
      build({
        text: 'KNOCHEN',
        font: 'oswald',
        seed: 55,
        type: { autoFitPercent: 0.9, weight: 500, tracking: 0.04 },
        background: { kind: 'solid', colorA: '#0a0a0c' },
        textVisible: false,
        layers: [
          {
            technique: 'skeleton',
            seed: 9,
            values: {
              resolution: 2,
              threshold: 0.24,
              simplify: 1.5,
              minLength: 4,
              width: 13,
              widthFromDistance: true,
              cap: 'round',
              colorMode: 'thickness',
              opacity: 1,
              palette: pal('acid', { reverse: true }),
            },
          },
        ],
      }),
  },

  {
    id: 'fall',
    name: 'Fall',
    note: 'Die Schrift zersplittert und fällt — die Bruchstücke bleiben am Boden liegen, wo sie zur Ruhe kamen.',
    build: () =>
      build({
        text: 'FALL',
        font: 'alfa-slab-one',
        canvas: [1080, 1080],
        seed: 808,
        type: { autoFitPercent: 0.9, offsetY: -0.12 },
        background: { kind: 'solid', colorA: '#0f0d0c' },
        textVisible: false,
        layers: [
          {
            technique: 'shatter',
            seed: 43,
            values: {
              // Few enough that a fragment is still a piece of a letter: at 260 the
              // splinters are narrower than a stem and the word stops being one.
              fragments: 32,
              pattern: 'voronoi',
              // A nudge, not a detonation, and stopped after a moment: the word is still
              // a word, just coming apart. Run to rest and every fragment ends in a heap
              // at the bottom — physically right, and no longer a picture of anything.
              explosion: 0,
              gravity: 0.35,
              restitution: 0.28,
              friction: 0.4,
              spin: 0.06,
              steps: 30,
              walls: true,
              style: 'fill',
              colorMode: 'height',
              opacity: 1,
              palette: pal('ember', { reverse: true }),
            },
          },
        ],
        post: { vignette: { amount: 0.4 } },
      }),
  },

  {
    id: 'faeden',
    name: 'Fäden',
    note: 'Jeder Buchstabe hängt an einer Schnur, vom Wind bewegt und in seiner Endlage eingefroren.',
    build: () =>
      build({
        text: 'HÄNGEN',
        font: 'dm-serif-display',
        canvas: [1080, 1350],
        seed: 246,
        type: { autoFitPercent: 0.62, offsetY: -0.3 },
        background: { kind: 'linear', colorA: '#f6f2ea', colorB: '#ded6c8', angle: 90 },
        textVisible: false,
        layers: [
          {
            technique: 'rope',
            seed: 15,
            values: {
              length: 420,
              lengthJitter: 0.45,
              segments: 26,
              stiffness: 0.92,
              anchor: 'top',
              gravity: 0.7,
              wind: 0.9,
              steps: 420,
              letterSize: 150,
              followRope: true,
              ropeWidth: 1.2,
              ropeColor: '#3a332b',
              palette: pal('earth'),
            },
          },
        ],
      }),
  },

  {
    id: 'bewuchs',
    name: 'Bewuchs',
    note: 'Moos setzt sich auf der Wetterseite der Buchstaben fest und wird dort dick, wo es Ruhe hat.',
    build: () =>
      build({
        text: 'ALT',
        font: 'unifraktur-maguntia',
        canvas: [1080, 1080],
        seed: 1349,
        type: { autoFitPercent: 0.74, tracking: 0.02 },
        background: { kind: 'solid', colorA: '#0c100c' },
        ink: { color: '#d8d2c2' },
        layers: [
          {
            technique: 'moss',
            seed: 21,
            values: {
              cells: 42000,
              growthRate: 1.2,
              roughness: 0.6,
              maxThickness: 46,
              directionBias: -0.45,
              side: 'outside',
              density: 2.2,
              colorMode: 'thickness',
              opacity: 0.92,
              palette: pal('moss'),
            },
          },
        ],
        post: { vignette: { amount: 0.5 }, grain: { amount: 0.14 } },
      }),
  },

  {
    id: 'gitternetz',
    name: 'Gitternetz',
    note: 'Ein Delaunay-Netz über der Kontur — technische Zeichnung eines Wortes.',
    build: () =>
      build({
        text: 'MODELL',
        font: 'space-mono',
        seed: 111,
        type: { autoFitPercent: 0.9, tracking: 0.04, weight: 700 },
        background: { kind: 'solid', colorA: '#071018' },
        ink: { renderMode: 'stroke', strokeWidth: 1.4, color: '#bcd8ee' },
        textOpacity: 0.8,
        layers: [
          {
            technique: 'delaunay',
            seed: 64,
            values: {
              points: 900,
              // Inside, clipped to the letters: the word *is* the mesh, rather than
              // sitting behind a net that spans the whole canvas and hides it.
              distribution: 'inside',
              style: 'wireframe',
              lineWidth: 0.8,
              clip: 'inside',
              colorMode: 'distance',
              opacity: 1,
              palette: pal('blueprint', { reverse: true }),
            },
          },
        ],
      }),
  },

  {
    id: 'text-aus-text',
    name: 'Text aus Text',
    note: 'Das Wort besteht aus lauter kleinen Wörtern, die seiner Kontur folgen.',
    build: () =>
      build({
        text: 'ECHO',
        font: 'anton',
        canvas: [1080, 1080],
        seed: 77,
        type: { autoFitPercent: 0.9, tracking: 0.01 },
        background: { kind: 'solid', colorA: '#0b0b0f' },
        textVisible: false,
        layers: [
          {
            technique: 'text-from-text',
            seed: 4,
            values: {
              path: 'contour',
              fontSize: 15,
              tracking: 0.04,
              contourOffset: 11,
              edge: 'clip',
              colorMode: 'depth',
              opacity: 1,
              palette: pal('pastel'),
            },
          },
        ],
      }),
  },

  {
    id: 'frost',
    name: 'Frost',
    note: 'Diffusionsbegrenzte Aggregation: Kristalle wachsen von der Schriftkante nach aussen.',
    build: () =>
      build({
        text: 'KALT',
        font: 'big-shoulders',
        seed: 273,
        type: { autoFitPercent: 0.86, weight: 700, tracking: 0.08 },
        background: { kind: 'radial', colorA: '#0a1420', colorB: '#03060c', radius: 1.2 },
        ink: { color: '#dceaf6' },
        textOpacity: 0.9,
        layers: [
          {
            technique: 'dla',
            seed: 51,
            values: {
              particles: 22000,
              stepSize: 1.8,
              stickiness: 0.9,
              seedMode: 'outline',
              direction: 'outward',
              textBlocks: true,
              connect: true,
              colorMode: 'time',
              opacity: 0.85,
              palette: pal('blueprint', { reverse: true }),
            },
          },
        ],
        post: { bloom: { threshold: 0.6, radius: 22, intensity: 0.45 } },
      }),
  },

  {
    id: 'streuung',
    name: 'Streuung',
    note: 'Poisson-Streuung mit variablem Abstand — dicht am Wort, licht am Rand, nie zwei Formen zu nah.',
    build: () =>
      build({
        text: 'RAUM',
        font: 'outfit',
        canvas: [1080, 1080],
        seed: 620,
        type: { autoFitPercent: 0.76, weight: 200, tracking: 0.3 },
        background: { kind: 'solid', colorA: '#faf7f2' },
        ink: { color: '#18181c' },
        layers: [
          {
            technique: 'poisson-scatter',
            seed: 6,
            values: {
              spacing: 30,
              gradient: 0.75,
              gradientReach: 340,
              exclusion: 16,
              region: 'outside',
              shape: 'mixed',
              rotationJitter: 90,
              style: 'fill',
              colorMode: 'distance',
              opacity: 0.9,
              palette: pal('bauhaus'),
            },
          },
        ],
      }),
  },

  {
    id: 'relief',
    name: 'Relief',
    note: 'Der Text als Landschaft: ein Höhenfeld aus Rauschen, aus flachem Winkel betrachtet.',
    build: () =>
      build({
        text: 'BERG',
        font: 'chivo',
        seed: 4004,
        type: { autoFitPercent: 0.84, weight: 800 },
        background: { kind: 'linear', colorA: '#0b0e18', colorB: '#020308', angle: 90 },
        textVisible: false,
        layers: [
          {
            technique: 'height-field',
            seed: 18,
            values: {
              amplitude: 230,
              noiseScale: 0.003,
              octaves: 4,
              resolution: 6,
              style: 'wireframe',
              lineWidth: 0.9,
              azimuth: 10,
              // Low over the horizon: from steeply above, a height field reads as a flat
              // map instead of a landscape.
              elevation: 20,
              distance: 1.1,
              colorMode: 'height',
              opacity: 0.95,
              palette: pal('cyberpunk', { reverse: true }),
            },
          },
        ],
        post: { bloom: { threshold: 0.65, radius: 26, intensity: 0.4 } },
      }),
  },

  {
    id: 'geflecht',
    name: 'Geflecht',
    note: 'Zwei Techniken übereinander: ein feines Streumuster unter dem harten Schnitt der Fragmente.',
    build: () =>
      build({
        text: 'SCHICHT',
        font: 'familjen-grotesk',
        seed: 1206,
        type: { autoFitPercent: 0.88, weight: 700, tracking: 0 },
        background: { kind: 'solid', colorA: '#101014' },
        textVisible: false,
        layers: [
          {
            technique: 'poisson-scatter',
            seed: 2,
            values: {
              spacing: 15,
              gradient: 0.6,
              region: 'all',
              shape: 'circle',
              style: 'stroke',
              lineWidth: 0.6,
              opacity: 0.35,
              colorMode: 'distance',
              palette: pal('mono', { reverse: true }),
            },
          },
          {
            technique: 'fragmentation',
            seed: 34,
            blendMode: 'screen',
            values: {
              fragments: 340,
              pattern: 'splinter',
              gap: 1,
              displacement: 16,
              displacementMode: 'radial',
              distanceFalloff: 0.8,
              rotation: 8,
              style: 'both',
              lineWidth: 0.8,
              colorMode: 'distance',
              opacity: 0.95,
              palette: pal('bauhaus'),
            },
          },
        ],
      }),
  },

  {
    id: 'schwarm',
    name: 'Schwarm',
    note: 'Buchstaben als Partikel, die dem Wort ausweichen und sich in ihre Flugrichtung drehen.',
    build: () =>
      build({
        text: 'FLUG',
        font: 'sora',
        seed: 909,
        type: { autoFitPercent: 0.74, weight: 600, tracking: 0.1 },
        background: { kind: 'solid', colorA: '#06070c' },
        ink: { color: '#f0f0f4' },
        textOpacity: 0.85,
        layers: [
          {
            technique: 'glyph-particles',
            seed: 71,
            values: {
              count: 5200,
              steps: 220,
              origin: 'outline',
              source: 'ownText',
              stepSize: 2.4,
              noiseScale: 0.0022,
              gravity: -0.05,
              textRepel: 2.1,
              textRadius: 150,
              rotateToMotion: true,
              rotationJitter: 22,
              opacity: 0.8,
              colorMode: 'distance',
              palette: pal('pastel'),
            },
          },
        ],
        post: { vignette: { amount: 0.35 } },
      }),
  },
];

export const STARTER_BY_ID = new Map(STARTER_PRESETS.map((p) => [p.id, p]));
